import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { projectsApi, tasksApi, workQueryKeys } from '@/features/work/api';
import type { TasksFilters, CreateTaskPayload, UpdateTaskPayload, ProjectOption } from '../types';

// ─── Queries ────────────────────────────────────────────────────────────────

export function useTasks(filters: TasksFilters = {}) {
  return useQuery({
    queryKey: workQueryKeys.tasks.list(filters),
    queryFn: () => tasksApi.list(filters),
    placeholderData: keepPreviousData,
  });
}

export function useTaskDetail(id: string | null) {
  return useQuery({
    queryKey: workQueryKeys.tasks.detail(id),
    queryFn: () => tasksApi.detail(id as string),
    enabled: !!id,
  });
}

export function useProjectOptions() {
  return useQuery({
    queryKey: workQueryKeys.projects.options,
    queryFn: async (): Promise<ProjectOption[]> => projectsApi.list(),
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useTaskMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: workQueryKeys.tasks.root });

  const createTask = useMutation({ mutationFn: (payload: CreateTaskPayload) => tasksApi.create(payload), onSuccess: invalidate });
  const updateTask = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateTaskPayload }) => tasksApi.update(id, payload),
    onSuccess: invalidate,
  });
  const deleteTask = useMutation({ mutationFn: (id: string) => tasksApi.remove(id), onSuccess: invalidate });
  const createProject = useMutation({
    mutationFn: (payload: { name: string; department: string }) => projectsApi.create(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: workQueryKeys.projects.root }),
  });

  return { createTask, updateTask, deleteTask, createProject };
}
